import React, { useState, useEffect, useRef } from 'react';
import { Send, Trash2, History, Plus, Square } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import { api } from '../../utils/api';
import './Chat.css';
import kigiAvatar from '../../assets/kigi-avatar.png';

function ChatPanel() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [totalTokens, setTotalTokens] = useState({ input: 0, output: 0 });
  const [typewriterText, setTypewriterText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);

  const [user, setUser] = useState({});

  useEffect(() => {
    // Fetch user data from API instead of localStorage
    api.getMe()
      .then(me => setUser(me))
      .catch(() => {});
    loadConversations();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = async () => {
    try {
      const convs = await api.getChatHistory();
      setConversations(convs);

      if (convs.length > 0) {
        const first = convs[0];
        setCurrentConversationId(first.id);
        setMessages(first.messages || []);
        setTotalTokens({
          input: first.input_tokens || 0,
          output: first.output_tokens || 0,
        });
      } else {
        setMessages([]);
        setCurrentConversationId(null);
        setTotalTokens({ input: 0, output: 0 });
      }
    } catch (e) {
      // Silently fail — user sees empty chat state
    }
  };

  const loadConversation = (conv) => {
    setMessages(conv.messages);
    setCurrentConversationId(conv.id);
    setTotalTokens(conv.totalTokens || { input: 0, output: 0 });
    setShowHistory(false);
  };

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    try {
      await api.deleteConversation(convId);
      const updated = conversations.filter(c => c.id !== convId);
      setConversations(updated);
      if (currentConversationId === convId) {
        startNewConversation();
      }
    } catch (err) {
      // Silently fail — conversation stays in list
    }
  };

  const startNewConversation = () => {
    setMessages([]);
    setCurrentConversationId(null);
    setTotalTokens({ input: 0, output: 0 });
    setShowHistory(false);
  };

  const stopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoading(false);
    setIsTyping(false);
    if (typewriterText) {
      // Keep partial response
      const partialMessage = { role: 'assistant', content: typewriterText + '... (stopped)' };
      setMessages(prev => [...prev, partialMessage]);
      setTypewriterText('');
    }
  };

  const typewriterEffect = async (text, callback) => {
    setIsTyping(true);
    setTypewriterText('');

    for (let i = 0; i <= text.length; i++) {
      if (abortControllerRef.current?.signal?.aborted) {
        setIsTyping(false);
        return;
      }
      setTypewriterText(text.substring(0, i));
      await new Promise(resolve => setTimeout(resolve, 12)); // 12ms per character (slightly faster)
    }

    setIsTyping(false);
    callback();
  };

  const handleSubmit = async (e, suggestedQuestion = null) => {
    e?.preventDefault();
    const question = suggestedQuestion || input;
    if (!question.trim() || isLoading) return;

    const userMessage = { role: 'user', content: question };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    // Create AbortController so the stop button can cancel the request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await api.sendMessage(question, currentConversationId, controller.signal);

      await typewriterEffect(response.answer, () => {
        const assistantMessage = { role: 'assistant', content: response.answer };
        const updatedMessages = [...newMessages, assistantMessage];
        setMessages(updatedMessages);

        setTotalTokens(prev => ({
          input: prev.input + (response.tokens?.input || 0),
          output: prev.output + (response.tokens?.output || 0),
        }));

        // Refresh history from backend to get new conv id + title
        loadConversations();
        if (response.conversation_id) {
          setCurrentConversationId(response.conversation_id);
        }

        setTypewriterText('');
      });
    } catch (error) {
      // Don't show error message if the user manually aborted the request
      if (error.name === 'AbortError') return;
      const errorMessage = {
        role: 'assistant',
        content: error.message || 'Sorry, I encountered an error.'
      };
      setMessages([...newMessages, errorMessage]);
    } finally {
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  };

  return (
    <div className="chat-panel-content">
      {/* Header */}
      <div className="chat-header-inline">
        <h2>Kigi AI</h2>
        <div className="chat-header-actions">
          <button
            className="icon-btn-sm"
            onClick={() => setShowHistory(!showHistory)}
            title="Chat History"
            aria-label="Toggle chat history"
          >
            <History size={18} />
            {conversations.length > 0 && (
              <span className="badge">{conversations.length}</span>
            )}
          </button>
          {messages.length > 0 && (
            <button
              className="icon-btn-sm"
              onClick={startNewConversation}
              title="New Chat"
              aria-label="Start new chat"
            >
              <Plus size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Token Counter – only show for admins */}
      {user.is_admin && (totalTokens.input > 0 || totalTokens.output > 0) && (
        <div className="token-counter">
          <span className="token-label">Tokens:</span>
          <span className="token-stat">↑ {totalTokens.input.toLocaleString()}</span>
          <span className="token-stat">↓ {totalTokens.output.toLocaleString()}</span>
          <span className="token-total">
            Total: {(totalTokens.input + totalTokens.output).toLocaleString()}
          </span>
        </div>
      )}

      {/* History Sidebar */}
      {showHistory && (
        <div className="history-overlay">
          <div className="history-panel">
            <div className="history-header">
              <h3>Chat History</h3>
              <button onClick={() => setShowHistory(false)}>✕</button>
            </div>
            <div className="history-list">
              {conversations.length === 0 ? (
                <div className="history-empty">
                  <History size={32} style={{ opacity: 0.3 }} />
                  <p>No conversations yet</p>
                </div>
              ) : (
                conversations.map(conv => (
                  <div key={conv.id} className="history-item">
                    <button 
                      className="history-btn"
                      onClick={() => loadConversation(conv)}
                    >
                      <div className="history-title">{conv.title}</div>
                      <div className="history-meta">
                        {new Date(conv.timestamp).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </div>
                    </button>
                    <button
                      className="delete-btn"
                      onClick={(e) => deleteConversation(conv.id, e)}
                      aria-label="Delete conversation"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div className="chat-messages-area">
        {messages.length === 0 ? (
          <div className="chat-empty-state">
            <img src={kigiAvatar} alt="Kigi" className="kigi-avatar-large" />
            <h3>Hi, I'm Kigi!</h3>
            <p>Your internal finance assistant. Ask me about your financials, cash flow, trends, or anything else I can help clarify.</p>
            <div className="suggested-prompts">
              <button
                className="prompt-chip"
                onClick={(e) => handleSubmit(e, 'Give me a summary of my current financial health including key metrics and any concerns')}
              >
                Financial health summary
              </button>
              <button
                className="prompt-chip"
                onClick={(e) => handleSubmit(e, 'Analyze my cash flow trends over the past few months and identify any patterns')}
              >
                Cash flow analysis
              </button>
              <button
                className="prompt-chip"
                onClick={(e) => handleSubmit(e, 'What are my biggest expenses and how have they changed recently?')}
              >
                Expense breakdown
              </button>
              <button
                className="prompt-chip"
                onClick={(e) => handleSubmit(e, 'Compare my revenue and profit margins month over month')}
              >
                Profitability insights
              </button>
              <button
                className="prompt-chip"
                onClick={(e) => handleSubmit(e, 'Are there any red flags or concerns I should be aware of in my financial data?')}
              >
                Risk assessment
              </button>
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, idx) => (
              <div key={idx} className={`chat-message ${msg.role}`}>
                <div className="message-avatar">
                  {msg.role === 'user' ? (
                    <span className="user-avatar">You</span>
                  ) : (
                    <img src={kigiAvatar} alt="Kigi" className="kigi-avatar-small" />
                  )}
                </div>
                <div className="message-content">
                  {msg.role === 'assistant' ? (
                    <ReactMarkdown rehypePlugins={[rehypeSanitize]}>{msg.content}</ReactMarkdown>
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}

            {/* Typewriter Display */}
            {isTyping && typewriterText && (
              <div className="chat-message assistant">
                <div className="message-avatar">
                  <img src={kigiAvatar} alt="Kigi" className="kigi-avatar-small" />
                </div>
                <div className="message-content typewriter">
                  <ReactMarkdown rehypePlugins={[rehypeSanitize]}>{typewriterText}</ReactMarkdown>
                  <span className="cursor">|</span>
                </div>
              </div>
            )}

            {/* Loading Indicator */}
            {isLoading && !isTyping && (
              <div className="chat-message assistant">
                <div className="message-avatar">
                  <img src={kigiAvatar} alt="Kigi" className="kigi-avatar-small" />
                </div>
                <div className="message-content typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="chat-input-wrapper">
        <form className="chat-input-area" onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Ask Kigi about your finances..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={isLoading}
          />
          {isLoading ? (
            <button type="button" onClick={stopGeneration} className="stop-btn" title="Stop generating" aria-label="Stop generating">
              <Square size={16} />
            </button>
          ) : (
            <button type="submit" disabled={!input.trim()} aria-label="Send message">
              <Send size={18} />
            </button>
          )}
        </form>
        <p className="chat-disclaimer">
          Kigi is an AI assistant and may make mistakes. Always verify important financial information.
        </p>
      </div>
    </div>
  );
}

export default ChatPanel;
